/**
 * CallLogsReportTable — detailed view for the Call Logs report.
 *
 * Searchable, sortable, paginated read-only table with column-visibility toggle
 * and CSV/PDF export (mirrors AlertDetailsTable). Columns:
 *   Call Start | Duration | Outcome | Talk Time | Call Type | Event | Caller | Receiver
 *
 * Outcome / Talk Time read the call_outcome / call_end_reason / talk_length columns
 * through utils/callOutcome.js (docs/TIME_TO_CONNECT_PLAN.md, phase 4). Calls logged
 * before those existed show a dash.
 *
 * Link rules:
 *   - Event   : /profiles/event/{event_id}  (when event_id present)
 *   - Caller  : /profiles/user/{caller_id}  (only when a caller name resolved)
 *   - Receiver: by callee_type_id —
 *       1 → /profiles/area/{area_id}       (area_id surfaced by the proc)
 *       2 → /profiles/user/{callee_id}
 *       3 → /profiles/resident/{callee_id}
 *     anything else → plain text
 */

import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { convertIsoUTCToLocal } from '../../../../libs.jsx';
import usePersistedPerPage from '../../../../hooks/usePersistedPerPage.js';
import usePersistedColumns from '../../../../hooks/usePersistedColumns.js';
import FilterPopover from '../../../../components/v2/FilterPopover.jsx';
import ActiveFilterBar from '../../../../components/v2/ActiveFilterBar.jsx';
import Pagination from '../../../../components/v2/Pagination.jsx';
import ColumnVisibilityToggle from '../../../../components/v2/ColumnVisibilityToggle.jsx';
import { exportCallLogsCsv, exportCallLogsPdf } from '../../../../services/csvExport.js';
import { compareValues } from '../../../../utils/sortRows.js';
import { ariaSort } from '../../../../components/v2/SortableTh.jsx';
import CallOutcome from '../../../../components/v2/CallOutcome.jsx';
import { callOutcomeLabel, callEndReasonLabel, callTalkLength } from '../../../../utils/callOutcome.js';

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------
const COLUMN_DEFS = [
    { key: 'call_start_time', label: 'Call Start', sortable: true, filterable: false },
    { key: 'call_length', label: 'Duration', sortable: true, filterable: false },
    { key: 'call_outcome', label: 'Outcome', sortable: true, filterable: true },
    { key: 'talk_length', label: 'Talk Time', sortable: true, filterable: false },
    { key: 'call_type_name', label: 'Call Type', sortable: true, filterable: true },
    { key: 'event_id', label: 'Event', sortable: true, filterable: false },
    { key: 'caller', label: 'Caller', sortable: true, filterable: true },
    { key: 'receiver', label: 'Receiver', sortable: true, filterable: true },
];
const ALL_COLUMN_KEYS = COLUMN_DEFS.map((c) => c.key);
const COLUMN_LABELS = {};
COLUMN_DEFS.forEach((c) => { COLUMN_LABELS[c.key] = c.label; });
// Added with phase 4: shown once to users whose saved column choice predates them.
const NEW_COLUMN_KEYS = ['call_outcome', 'talk_length'];

/* Outcome filters, sorts and searches on what the cell shows; legacy rows as "(empty)",
   like any other blank value in this table. */
const outcomeValue = (row) => callOutcomeLabel(row) || '(empty)';

// callee_type_id → profile route builder. Returns null when no link applies.
const buildReceiverLink = (row) => {
    const type = parseInt(row.callee_type_id, 10);
    if (type === 1 && row.area_id) return `/profiles/area/${row.area_id}`;
    if (type === 2 && row.callee_id) return `/profiles/user/${row.callee_id}`;
    if (type === 3 && row.callee_id) return `/profiles/resident/${row.callee_id}`;
    return null;
};

// Per-cell rendering (links/formatting) by column key.
function renderCell(row, colKey, tzOffset) {
    switch (colKey) {
        case 'call_start_time':
            return <span className="dp-cell-text dp-cell-text--muted" style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }}>{convertIsoUTCToLocal(row.call_start_time, tzOffset) || '—'}</span>;
        case 'call_length':
            return <span className="dp-cell-text" style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }}>{row.call_length || '—'}</span>;
        case 'call_outcome':
            return <CallOutcome call={row} />;
        case 'talk_length': {
            const talk = callTalkLength(row);
            return talk
                ? <span className="dp-cell-text" style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-xs)' }}>{talk}</span>
                : <span className="dp-cell-text dp-cell-text--muted">—</span>;
        }
        case 'call_type_name':
            return <span className="dp-cell-text">{row.call_type_name || '—'}</span>;
        case 'event_id':
            return row.event_id ? (
                <Link to={`/profiles/event/${row.event_id}`} className="dp-cell-text dp-cell-text--link" style={{ textDecoration: 'none' }}>{row.event_id}</Link>
            ) : (
                <span className="dp-cell-text dp-cell-text--muted">—</span>
            );
        case 'caller': {
            // The proc resolves `caller` (display_name) by joining core_user on
            // caller_id, so a resolved name means the caller is a user — link it.
            const callerLinkable = Boolean(row.caller_id) && Boolean(row.caller);
            return callerLinkable ? (
                <Link to={`/profiles/user/${row.caller_id}`} className="dp-cell-text dp-cell-text--link" style={{ textDecoration: 'none' }}>{row.caller}</Link>
            ) : (
                <span className="dp-cell-text">{row.caller || '—'}</span>
            );
        }
        case 'receiver': {
            const receiverLink = buildReceiverLink(row);
            return row.receiver && receiverLink ? (
                <Link to={receiverLink} className="dp-cell-text dp-cell-text--link" style={{ textDecoration: 'none' }}>{row.receiver}</Link>
            ) : (
                <span className="dp-cell-text">{row.receiver || '—'}</span>
            );
        }
        default:
            return <span className="dp-cell-text">{row[colKey] != null ? String(row[colKey]) : '—'}</span>;
    }
}

const CallLogsReportTable = ({ records, tzOffset, dateRangeLabel = '' }) => {
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({}); // { columnKey: string[] }
    const [sortCol, setSortCol] = useState('call_start_time');
    const [sortDir, setSortDir] = useState('desc');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = usePersistedPerPage();
    const [visibleColumns, handleColumnToggle] = usePersistedColumns('report-call-logs', ALL_COLUMN_KEYS, ALL_COLUMN_KEYS, NEW_COLUMN_KEYS);

    const visibleColDefs = useMemo(
        () => COLUMN_DEFS.filter((c) => visibleColumns.includes(c.key)),
        [visibleColumns],
    );

    // Unique values for a filterable column, derived from the FULL record set
    // (not the search/filter-narrowed view) so options never disappear.
    const getFilterValues = useCallback((columnKey) => {
        const values = new Set();
        (records || []).forEach((row) => {
            if (columnKey === 'call_outcome') { values.add(outcomeValue(row)); return; }
            const val = row[columnKey];
            values.add(val != null && val !== '' ? String(val) : '(empty)');
        });
        return [...values].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    }, [records]);

    const handleFilterChange = useCallback((columnKey, values) => {
        setFilters((prev) => ({ ...prev, [columnKey]: values }));
        setPage(1);
    }, []);

    const handleRemoveFilter = useCallback((columnKey, value) => {
        setFilters((prev) => ({ ...prev, [columnKey]: (prev[columnKey] || []).filter((v) => v !== value) }));
        setPage(1);
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setFilters({});
        setPage(1);
    }, []);

    const filtered = useMemo(() => {
        let rows = [...(records || [])];

        if (search) {
            const q = search.toLowerCase();
            rows = rows.filter((r) =>
                (r.call_type_name || '').toLowerCase().includes(q) ||
                (r.caller || '').toLowerCase().includes(q) ||
                (r.receiver || '').toLowerCase().includes(q) ||
                (r.event_id || '').toString().toLowerCase().includes(q) ||
                (r.call_length || '').toString().toLowerCase().includes(q) ||
                callOutcomeLabel(r).toLowerCase().includes(q) ||
                callEndReasonLabel(r).toLowerCase().includes(q) ||
                (r.call_start_time || '').toLowerCase().includes(q)
            );
        }

        // Per-column filters (Call Type / Caller / Receiver)
        Object.entries(filters).forEach(([columnKey, vals]) => {
            if (!vals || vals.length === 0) return;
            rows = rows.filter((row) => {
                if (columnKey === 'call_outcome') return vals.includes(outcomeValue(row));
                const val = row[columnKey];
                const strVal = val != null && val !== '' ? String(val) : '(empty)';
                return vals.includes(strVal);
            });
        });

        /* Blank values sort last in both directions, and a real 0 is no longer
           mistaken for blank (utils/sortRows.js). */
        // Outcome sorts by its label; Talk Time by what the column shows (blank last).
        const sortValue = (row) => (sortCol === 'call_outcome' ? (callOutcomeLabel(row) || null)
            : sortCol === 'talk_length' ? callTalkLength(row) : row[sortCol]);
        rows.sort((a, b) => compareValues(sortValue(a), sortValue(b), sortDir));

        return rows;
    }, [records, search, filters, sortCol, sortDir]);

    // Pagination math
    const totalFiltered = filtered.length;
    const totalPages = Math.max(1, Math.ceil(totalFiltered / perPage));
    const safePage = Math.min(page, totalPages);
    const startIdx = (safePage - 1) * perPage;
    const endIdx = Math.min(startIdx + perPage, totalFiltered);
    const pageRows = filtered.slice(startIdx, endIdx);

    const handleSort = useCallback((col) => {
        if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        else { setSortCol(col); setSortDir('asc'); }
        setPage(1);
    }, [sortCol]);

    // Export the full filtered/sorted set (not just the current page).
    const handleExportCsv = useCallback(() => {
        if (filtered.length) exportCallLogsCsv(filtered, 'call-logs.csv', visibleColumns, tzOffset);
    }, [filtered, visibleColumns, tzOffset]);

    const handleExportPdf = useCallback(() => {
        if (filtered.length) exportCallLogsPdf(filtered, 'Call Logs Report', dateRangeLabel, 'call-logs.pdf', visibleColumns, tzOffset);
    }, [filtered, visibleColumns, tzOffset, dateRangeLabel]);

    const SortIcon = ({ col }) => {
        if (sortCol !== col) return <span className="dp-sort-icon dp-sort-icon--neutral dp-icon-xs">↕</span>;
        return <span className="dp-sort-icon dp-sort-icon--active dp-icon-xs">{sortDir === 'asc' ? '↑' : '↓'}</span>;
    };

    return (
        <div className="pp-section">
            <div className="pp-section__header">
                <h3 className="pp-section__title">Call Records</h3>
                <div className="pp-section__actions">
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-tertiary)' }}>
                        {filtered.length} {filtered.length === 1 ? 'call' : 'calls'}
                    </span>
                </div>
            </div>
            <div className="pp-section__body">
                <div className="dp-controls">
                    <div className="dp-controls__left">
                        <div className="dp-search">
                            <svg className="dp-icon-md dp-search__icon" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                            </svg>
                            <input
                                className="dp-search__input"
                                type="text"
                                placeholder="Search calls…"
                                value={search}
                                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            />
                        </div>
                    </div>
                    <div className="dp-controls__right">
                        <ColumnVisibilityToggle
                            columns={COLUMN_DEFS}
                            visibleColumns={visibleColumns}
                            onToggle={handleColumnToggle}
                        />
                        <button type="button" className="rp-btn rp-btn--secondary" onClick={handleExportCsv} disabled={!filtered.length} aria-label="Export call logs to CSV">
                            <i className="fe fe-download" style={{ marginRight: '4px' }} />CSV
                        </button>
                        <button type="button" className="rp-btn rp-btn--secondary" onClick={handleExportPdf} disabled={!filtered.length} aria-label="Export call logs to PDF">
                            <i className="fe fe-file-text" style={{ marginRight: '4px' }} />PDF
                        </button>
                    </div>
                </div>

                {/* Active filter chips */}
                <ActiveFilterBar
                    filters={filters}
                    columnLabels={COLUMN_LABELS}
                    onRemoveFilter={handleRemoveFilter}
                    onClearAll={handleClearAllFilters}
                />

                {(records || []).length === 0 ? (
                    <div className="dp-empty" style={{ marginTop: 'var(--space-4)' }}>
                        <p className="dp-empty__title">No calls</p>
                        <p className="dp-empty__text">No calls were recorded for the selected period.</p>
                    </div>
                ) : (
                    <>
                        <div className="dp-table-wrap" style={{ marginTop: 'var(--space-3)' }}>
                            <table className="dp-table">
                                <thead className="dp-thead">
                                    <tr>
                                        {visibleColDefs.map((col) => (
                                            <th key={col.key} className="dp-th" aria-sort={col.sortable ? ariaSort(sortCol, sortDir, col.key) : undefined}>
                                                <div className="dp-th__content">
                                                    {col.sortable ? (
                                                        <button className="dp-th__sort-btn" onClick={() => handleSort(col.key)}>
                                                            {col.label} <SortIcon col={col.key} />
                                                        </button>
                                                    ) : (
                                                        <span>{col.label}</span>
                                                    )}
                                                    {col.filterable ? (
                                                        <FilterPopover
                                                            columnKey={col.key}
                                                            label={col.label}
                                                            values={getFilterValues(col.key)}
                                                            selectedValues={filters[col.key] || []}
                                                            onChange={handleFilterChange}
                                                        />
                                                    ) : null}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {pageRows.map((row, idx) => (
                                        <tr key={row.call_id || idx} className="dp-tr">
                                            {visibleColDefs.map((col) => (
                                                <td key={col.key} className="dp-td">
                                                    {renderCell(row, col.key, tzOffset)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <Pagination
                            page={safePage}
                            totalPages={totalPages}
                            total={totalFiltered}
                            perPage={perPage}
                            onPageChange={setPage}
                            onPerPageChange={(n) => { setPerPage(n); setPage(1); }}
                            entityLabel={totalFiltered === 1 ? 'call' : 'calls'}
                            selectId="cl-per-page"
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default CallLogsReportTable;
